import { DateLocalizer } from 'react-big-calendar';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import { eq } from 'react-big-calendar/lib/utils/dates';

dayjs.extend(isBetween);
dayjs.extend(localizedFormat);
dayjs.extend(localeData);
dayjs.extend(timezone);
dayjs.extend(duration);

const dateRangeFormat = ({ start, end }, culture, local) =>
  `${local.format(start, 'L', culture)} – ${local.format(end, 'L', culture)}`;

const timeRangeFormat = ({ start, end }, culture, local) =>
  `${local.format(start, 'LT', culture)} – ${local.format(end, 'LT', culture)}`;

const timeRangeStartFormat = ({ start }, culture, local) =>
  `${local.format(start, 'LT', culture)} – `;

const timeRangeEndFormat = ({ end }, culture, local) => ` – ${local.format(end, 'LT', culture)}`;

const weekRangeFormat = ({ start, end }, culture, local) =>
  `${local.format(start, 'MMMM DD', culture)} – ${local.format(
    end,
    eq(start, end, 'month') ? 'DD' : 'MMMM DD',
    culture,
  )}`;

const formats = {
  dateFormat: 'DD',
  dayFormat: 'DD ddd',
  weekdayFormat: 'ddd',

  selectRangeFormat: timeRangeFormat,
  eventTimeRangeFormat: timeRangeFormat,
  eventTimeRangeStartFormat: timeRangeStartFormat,
  eventTimeRangeEndFormat: timeRangeEndFormat,

  timeGutterFormat: 'LT',

  monthHeaderFormat: 'MMMM YYYY',
  dayHeaderFormat: 'dddd MMM DD',
  dayRangeHeaderFormat: weekRangeFormat,
  agendaHeaderFormat: dateRangeFormat,

  agendaDateFormat: 'ddd MMM DD',
  agendaTimeFormat: 'LT',
  agendaTimeRangeFormat: timeRangeFormat,
};

const dayjsLocalizerCreator = () => {
  const locale = (m, c) => (c ? m.locale(c) : m);

  return new DateLocalizer({
    formats,
    firstOfWeek() {
      const data = dayjs.localeData();

      return data ? data.firstDayOfWeek() : 0;
    },

    format(value, format, culture) {
      return locale(dayjs(value), culture).format(format);
    },
  });
};

export const isRangeOverlapped = ({ start, end }, ranges) =>
  ranges.some(range => dayjs(start).isBefore(range.end) && dayjs(range.start).isBefore(end));

export const isDateBetweenRange = ({
  start,
  end,
  date,
  precision = 'day',
  inclusionOption = '[]',
}) => dayjs(date).isBetween(start, end, precision, inclusionOption);

export const dayjsLocalizer = dayjsLocalizerCreator();

// Needed to handle situation when BE sends dates with [ms] more than 3 chars and we can't lead it to ISO
export const replaceTimezonePostfix = date => date.replace('+00:00', 'Z');

const checkIsDateStringWithTimezoneOffset = string =>
  /[zZ]|([+-])([01]\d|2[0-3]):?([0-5]\d)/.test(string);

export const formatDateStringWithDefaultOffset = string => {
  const isInvalidDate = !string || !dayjs(string).isValid();

  if (isInvalidDate || checkIsDateStringWithTimezoneOffset(string)) {
    return string;
  }

  return `${string}Z`;
};

export const formattedYears = years => (years > 0 ? `${years} year${years === 1 ? '' : 's'}` : '');
